import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {MouseEventHandler, MouseEvent, ReactNode} from 'react';

import {UserSummary} from 'constants/UserConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Tooltip, {TooltipPlacement, TooltipWrapper} from 'ui-library/bs/Tooltip';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as nyc from 'ui-library/NYC';
import PartyAvatarList from 'ui-library/PartyAvatarList';

import StarIcon from '../StarIcon';

export interface SidepanelItemProps {
  active?: boolean;
  className?: string;
  faded?: boolean;
  handleClickStar?: () => void;
  icon?: ReactNode;
  isCompact: boolean;
  onClick: MouseEventHandler;
  onContextMenu?: MouseEventHandler;
  parties: Array<UserSummary>;
  showStar?: boolean;
  sampleStatusIcon?: ReactNode;
  starred?: boolean;
  subTitle?: ReactNode;
  title: {
    text: string;
    node?: ReactNode;
  };
  tooltipText?: string;
  isExpandable: boolean;
  isExpanded?: boolean;
  // Signifies if this is a child of an expandable item.
  isChild: boolean;
}
class SidepanelItem extends React.Component<SidepanelItemProps> {
  static defaultProps = {
    active: false,
    faded: false,
    parties: [],
    showStar: false,
    starred: false,
  };

  private handleClickStar = (e: MouseEvent): void => {
    e.stopPropagation();
    this.props.handleClickStar!();
  };

  private renderSubHeader = (): JSX.Element | null => {
    if (this.props.subTitle == null) {
      return null;
    }
    return (
      <span className="sidepanelItem-subTitle">
        <span className="sidepanelItem-subTitle-text">{this.props.subTitle}</span>
        {!this.props.isCompact && (
          <PartyAvatarList
            maxAvatars={3}
            parties={this.props.parties}
            size={AvatarSize.XXSMALL}
            tooltipPlacement={FloatingPlacement.BOTTOM}
          />
        )}
      </span>
    );
  };

  // If `this.props.isCompact` is true and `this.props.showStar` is false,
  // `subHeaderElement` will be rendered inline here instead of below the header
  private renderHeader = (subHeaderElement: JSX.Element | null): JSX.Element => {
    let inlineElement: JSX.Element | null = null;
    if (this.props.showStar) {
      inlineElement = (
        <span className={css(styles.inlineElement)} id="inlineElement">
          {this.props.sampleStatusIcon ?? null}
          <StarIcon onClick={this.handleClickStar} starred={this.props.starred} />
        </span>
      );
    } else if (this.props.isCompact) {
      inlineElement = subHeaderElement;
    }

    return (
      <div className="u-lr-split">
        <span
          className={classNames('sidepanelItem-title', 'u-overflow-wrap-break-word', css(styles.title), {
            'u-ellipsis-text': this.props.isCompact,
          })}
          {...getTestProps({component: 'SidepanelItem', element: 'root'})}
        >
          <Tooltip title={this.props.title.text}>
            {this.props.title.node || <span>{this.props.title.text}</span>}
          </Tooltip>
        </span>
        {inlineElement}
      </div>
    );
  };

  render(): JSX.Element {
    const subHeaderElement = this.renderSubHeader();
    return (
      <TooltipWrapper tooltipTitle={this.props.tooltipText} tooltipPlacement={TooltipPlacement.RIGHT}>
        <div
          className={classNames('sidepanelItem', this.props.className, {
            'sidepanelItem--compact': this.props.isCompact,
            'sidepanelItem--faded': this.props.faded,
            'is-active': this.props.active,
          })}
          onClick={this.props.onClick}
          onContextMenu={this.props.onContextMenu}
        >
          <nyc.Row>
            <nyc.Col
              className={classNames(
                'sidepanelItem-content',
                this.props.isChild && 'sidepanelItem-childPadding'
              )}
              sm={6}
            >
              {this.props.isExpandable && (
                <div className="sidepanelItem-disclosureTriangle">
                  <BenchlingIcon icon={this.props.isExpanded ? 'triangle-down' : 'triangle-right'} />
                </div>
              )}
              {this.props.icon && <div className="sidepanelItem-icon">{this.props.icon}</div>}
              <div className="sidepanelItem-text">
                {this.renderHeader(subHeaderElement)}
                {!this.props.isCompact && <div>{subHeaderElement}</div>}
              </div>
            </nyc.Col>
          </nyc.Row>
        </div>
      </TooltipWrapper>
    );
  }
}

export default SidepanelItem;

export const styles = StyleSheet.create({
  inlineElement: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '60px',
  },
  title: {
    width: '100%',
  },
});
